import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  FileDown,
  ExternalLink,
  Loader2,
  AlertCircle,
  Home,
  FileText,
  Sparkles,
  PlayCircle,
  Video,
} from "lucide-react";
import { API } from "@/lib/checkout";
import { useNoIndex } from "@/hooks/useNoIndex";
import { trackEvent } from "@/lib/analytics";
import { SiteHeader } from "@/components/Brand";

export default function Access() {
  useNoIndex();
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | invalid | revoked
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/access/${token}`);
        if (res.status === 404) return !cancelled && setState("invalid");
        if (res.status === 410) return !cancelled && setState("revoked");
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setState("ready");
        }
      } catch (e) {
        if (!cancelled) setState("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const openAsset = async (asset) => {
    setBusy(asset.id);
    try {
      if (asset.type === "notion_link") {
        trackEvent("notion_link_clicked", { asset: asset.id });
        const res = await fetch(`${API}/access/${token}/notion-link`);
        const json = await res.json();
        if (json.url) window.open(json.url, "_blank", "noopener");
      } else {
        trackEvent("download_completed", { asset: asset.id });
        const res = await fetch(`${API}/access/${token}/assets/${asset.id}/download`);
        const json = await res.json();
        if (json.url) {
          const url = json.url.startsWith("/")
            ? `${process.env.REACT_APP_BACKEND_URL}${json.url}`
            : json.url;
          window.open(url, "_blank", "noopener");
        }
      }
    } catch (e) {
      alert("Could not open that item. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B132B]">
        <Loader2 className="h-10 w-10 animate-spin text-[#F5A623]" />
      </div>
    );
  }

  if (state === "invalid" || state === "revoked") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B132B] px-6" data-testid="access-error">
        <div className="max-w-[480px] rounded-3xl border border-white/10 bg-[#14213D] p-10 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-[#FF6B6B]" />
          <h1 className="font-heading mt-5 text-2xl font-extrabold text-white">
            {state === "revoked" ? "This access link is no longer active" : "This access link is not valid"}
          </h1>
          <p className="mt-3 text-[#BFD4E8]">
            {state === "revoked"
              ? "Access for this order has been revoked. If you believe this is a mistake, contact support."
              : "Double-check the link from your confirmation email, or head back to the homepage."}
          </p>
          <Link
            to="/"
            className="mt-7 inline-flex items-center gap-2 rounded-xl border border-white/20 px-6 py-3 font-heading font-bold text-white transition-colors hover:bg-white/10"
          >
            <Home className="h-4 w-4" /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B132B]" data-testid="access-page">
      <SiteHeader />
      <div className="px-6 py-16">
      <div className="mx-auto max-w-[720px]">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#3A7CA5]/40 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#A8D5F5]">
          <Sparkles className="h-4 w-4" /> Your Downloads
        </div>
        <h1 className="font-heading mt-6 text-3xl md:text-4xl font-extrabold text-white">
          {data.product.name}
        </h1>
        <p className="mt-3 text-[#BFD4E8]">
          Everything you purchased is below. Download the PDFs and duplicate the
          Notion template to your own workspace. Start with a single anchor task
          tomorrow morning.
        </p>

        <div className="mt-10 space-y-4">
          {data.assets.filter((a) => !["ast_userguide", "ast_license"].includes(a.id)).map((asset) => (
            <div
              key={asset.id}
              data-testid={`asset-${asset.id}`}
              className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-[#14213D] p-6 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-center gap-4">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#F5A623]/15 text-[#F5A623]">
                  {asset.type === "notion_link" ? (
                    <ExternalLink className="h-5 w-5" />
                  ) : (
                    <FileText className="h-5 w-5" />
                  )}
                </span>
                <p className="font-heading font-bold text-white">{asset.displayName}</p>
              </div>
              <button
                type="button"
                onClick={() => openAsset(asset)}
                disabled={busy === asset.id}
                data-testid={`asset-action-${asset.id}`}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F5A623] px-5 py-2.5 font-heading text-sm font-bold text-[#0B132B] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70"
              >
                {busy === asset.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : asset.type === "notion_link" ? (
                  <>
                    Open Notion <ExternalLink className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    Download <FileDown className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        {data.videos && data.videos.items?.length > 0 && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-[#14213D] p-6" data-testid="access-videos">
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#3A7CA5]/20 text-[#A8D5F5]">
                <Video className="h-5 w-5" />
              </span>
              <div>
                <p className="font-heading font-bold text-white">Setup videos</p>
                <p className="text-sm text-[#9FBCD6]">Five short walkthroughs so setup never stalls.</p>
              </div>
            </div>
            <ul className="mt-5 space-y-2.5">
              {data.videos.items.map((v, i) => (
                <li key={i} data-testid={`video-item-${i}`}>
                  <a
                    href={`/guides#guide-${i + 1}`}
                    onClick={() => trackEvent("setup_video_opened", { title: v.title })}
                    data-testid={`video-link-${i}`}
                    className="group flex items-center gap-3 rounded-lg px-2 py-1.5 text-[#D6E2F0] transition-colors hover:bg-white/5"
                  >
                    <PlayCircle className="h-4 w-4 flex-shrink-0 text-[#F5A623]" />
                    <span className="text-[15px] group-hover:text-white">{v.title}</span>
                    <ExternalLink className="ml-auto h-3.5 w-3.5 flex-shrink-0 text-[#5A7A94] opacity-0 transition-opacity group-hover:opacity-100" />
                  </a>
                </li>
              ))}
            </ul>
            <a
              href="/guides"
              onClick={() => trackEvent("setup_videos_opened")}
              data-testid="access-videos-open"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl border border-[#3A7CA5]/50 px-5 py-2.5 font-heading text-sm font-bold text-[#A8D5F5] transition-transform duration-200 hover:-translate-y-0.5"
            >
              Open the guided walkthroughs <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}

        <p className="mt-10 text-center text-xs text-[#5A7A94]">
          Keep this link private. It grants access to your purchase. Need help?
          Reply to your confirmation email.
        </p>
      </div>
      </div>
    </div>
  );
}
